import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { createSearchParams, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useZocksIntercom } from '../../components/Intercom/hooks/useZocksIntercom'
import { CreateUserIntegrationPayload, useCreateUserIntegrationMutation } from '../../store/user/user'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { createState, IntegrationServiceId, readState, usesCustomDomainSSO } from '../../common/integrations'
import { addToast } from '../../store/toast/toastSlice'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { ROUTER_BASEPATH } from '../../common/constants'
import { type ApiErrorResponse } from '../../models/ApiError'
import { RedirectProgress } from '../../components/RedirectProgress/RedirectProgress'

type OAuthParams = {
  name: string
  required: boolean
  alias?: string
}

const _VALID_ZOHO_OAUTH_PARAMS: OAuthParams[] = [
  { name: 'location', required: false, alias: 'region' },
  { name: 'accounts-server', required: false, alias: 'accountsServer' },
]

const _VALID_DELEGATED_EMAIL_PARAMS: OAuthParams[] = [{ name: 'verification_code', required: true, alias: 'code' }]

const _INTEGRATIONS_EXCLUDE_GENERAL_OAUTH_PARAMS = new Set<IntegrationServiceId>([IntegrationServiceId.DELEGATED_EMAIL])

const _VALID_OAUTH_PARAMS: {
  [key in keyof typeof IntegrationServiceId | string]: OAuthParams[]
} = {
  general: [{ name: 'code', required: true }],
  [IntegrationServiceId.ZOHO]: _VALID_ZOHO_OAUTH_PARAMS,
  [IntegrationServiceId.DELEGATED_EMAIL]: _VALID_DELEGATED_EMAIL_PARAMS,
}

const _INTEGRATION_TYPE_MAP: { [key: string]: string } = {
  eMoney: 'emoney',
}

const _STATELESS_INTEGRATION_CONNECT_TYPES: Partial<Record<IntegrationServiceId, string>> = {
  [IntegrationServiceId.DELEGATED_EMAIL]: 'delegated_email',
}

const _INTEGRATION_SUCCESS_TOAST_MAP: Partial<Record<IntegrationServiceId, { title: string; message: string }>> = {
  [IntegrationServiceId.DELEGATED_EMAIL]: {
    title: 'Email address confirmed',
    message: 'This address is now linked to this Zocks account.',
  },
}

const isStatelessIntegrationType = (integrationType: IntegrationServiceId): boolean => Object.hasOwn(_STATELESS_INTEGRATION_CONNECT_TYPES, integrationType)

const getValidOAuthParams = (integrationType: string): OAuthParams[] => {
  const integrationParams = _VALID_OAUTH_PARAMS[integrationType] || []
  const excludeGeneral = _INTEGRATIONS_EXCLUDE_GENERAL_OAUTH_PARAMS.has(integrationType as IntegrationServiceId)
  return excludeGeneral ? integrationParams : _VALID_OAUTH_PARAMS['general'].concat(integrationParams)
}

const CODE_HAS_BEEN_USED_ERROR_CODE = 1004
const CODE_EXPIRED_ERROR_CODE = 1005

const _DEFAULT_ERROR_TOAST = {
  title: 'Something went wrong',
  message: 'Failed to create integration. Please contact us at support@zocks.io',
}

const _ERROR_CODE_TOAST_MAP: Partial<
  Record<
    IntegrationServiceId,
    Record<
      number,
      {
        title: string
        message: string
      }
    >
  >
> = {
  [IntegrationServiceId.DELEGATED_EMAIL]: {
    [CODE_HAS_BEEN_USED_ERROR_CODE]: {
      title: 'This link has already been used',
      message: 'Check your linked emails or request a new verification link.',
    },
    [CODE_EXPIRED_ERROR_CODE]: {
      title: 'This link has expired',
      message: 'Verification links expire after 24 hours. Go to your Zocks settings to send a new one.',
    },
  },
}

const IntegrationCallbackView: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { integrationType = IntegrationServiceId.ZOOM } = useParams()
  const accountId = useAppSelector(selectAccountId)
  const { booted } = useZocksIntercom()
  const [searchParams, setSearchParams] = useSearchParams()
  const [createUserIntegration, { isSuccess, isError, error }] = useCreateUserIntegrationMutation()
  const redirectingRef = useRef(false)
  const { tracked } = useTracking()
  const _createNewUserIntegration = useCallback(
    (accountId: string, integrationType: IntegrationServiceId, payload: CreateUserIntegrationPayload) => {
      createUserIntegration({
        accountId,
        integrationName: integrationType,
        payload,
      })
    },
    [createUserIntegration],
  )

  const createNewUserIntegration = useMemo(
    () =>
      tracked(_createNewUserIntegration, {
        eventName: TrackEvent.CONNECT_TO_SYSTEM,
        eventDetails: { serviceId: integrationType },
      }),
    [tracked, _createNewUserIntegration, integrationType],
  )

  const handleError = useCallback(() => {
    const error = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')
    const errorMessage = error
      ? `Service error: ${error}` + (errorDescription ? `: ${errorDescription}` : '')
      : 'Unable to create integration. Please contact us at support@zocks.io'
    dispatch(
      addToast({
        variant: 'error',
        title: 'Failed to create integration',
        messages: [errorMessage],
        delay: 10000,
      }),
    )
    console.log(searchParams.toString())
    navigate('/account/profile-settings/integrations')
  }, [dispatch, searchParams, navigate])

  useEffect(() => {
    if (Object.hasOwn(_INTEGRATION_TYPE_MAP, integrationType)) {
      const mappedIntegrationType = _INTEGRATION_TYPE_MAP[integrationType]
      navigate({
        pathname: `/integrations/callback/${mappedIntegrationType}`,
        search: createSearchParams(searchParams).toString(),
      })
    }
  }, [integrationType, navigate, searchParams])

  useEffect(() => {
    if (isSuccess) {
      const validParams = getValidOAuthParams(integrationType)
      validParams.forEach(({ name }) => searchParams.delete(name))
      searchParams.delete('app')
      setSearchParams(searchParams, { replace: true })
      redirectingRef.current = true

      if (isStatelessIntegrationType(integrationType as IntegrationServiceId)) {
        const successToast = _INTEGRATION_SUCCESS_TOAST_MAP[integrationType as IntegrationServiceId]
        if (successToast) {
          dispatch(addToast({ variant: 'success', title: successToast.title, messages: [successToast.message] }))
        }
        navigate(`/account/integration-settings/integrations/${integrationType as IntegrationServiceId}/settings`)
      } else {
        const state = searchParams.get('state')
        if (!state) {
          handleError()
          return
        }
        const stateData = readState(state, integrationType as IntegrationServiceId)
        if (!stateData) {
          handleError()
          return
        }
        const redirectUri = stateData.redirectUri
        if (redirectUri.endsWith('upcoming')) {
          window.location.href = `${redirectUri}?isNewIntegration`
        } else {
          window.location.href = redirectUri
        }
      }
    }
  }, [isSuccess, searchParams, setSearchParams, integrationType, booted, dispatch, navigate, handleError])

  useEffect(() => {
    if (!isError) return

    // Integrations that aren't self-handled already get an error toast from the global error middleware,
    // so we only have to leave the redirect screen — the toast is rendered by the layout we land on.
    if (!isStatelessIntegrationType(integrationType as IntegrationServiceId)) {
      navigate('/account/profile-settings/integrations', { replace: true })
      return
    }

    const errorCode = error && 'data' in error ? (error.data as ApiErrorResponse)?.errors?.[0]?.errorCode : undefined
    const { title, message } = (errorCode && _ERROR_CODE_TOAST_MAP[integrationType as IntegrationServiceId]?.[errorCode]) || _DEFAULT_ERROR_TOAST

    dispatch(addToast({ variant: 'error', title, messages: [message], delay: 10000 }))
    navigate(`/account/integration-settings/integrations/${integrationType}/settings`)
  }, [isError, error, integrationType, dispatch, navigate])

  const createPayloadFromSearchParams = useCallback(() => {
    const missingRequired: string[] = []
    const validParams = getValidOAuthParams(integrationType)
    const result = validParams.reduce((result: { [key: string]: string }, { name, required, alias }) => {
      const param = searchParams.get(name)

      if (!param && required) {
        missingRequired.push(name)
      }

      if (param) {
        result[alias || name] = param
      }
      return result
    }, {})
    if (missingRequired.length > 0) {
      console.error(`Missing required parameters: ${missingRequired.join(', ')}`)
    }

    return result
  }, [searchParams, integrationType])

  useEffect(() => {
    if (redirectingRef.current || Object.hasOwn(_INTEGRATION_TYPE_MAP, integrationType)) {
      return
    }
    const { code, ...rest } = createPayloadFromSearchParams()
    const mappedIntegrationType = Object.keys(_INTEGRATION_TYPE_MAP).find((e) => _INTEGRATION_TYPE_MAP[e] === integrationType) ?? integrationType
    const payloadRedirectUri = window.location.origin + ROUTER_BASEPATH + `/integrations/callback/${mappedIntegrationType as IntegrationServiceId}`
    if (isStatelessIntegrationType(integrationType as IntegrationServiceId)) {
      if (code) {
        const statelessRedirectUri =
          window.location.origin + ROUTER_BASEPATH + `/account/integration-settings/integrations/${mappedIntegrationType as IntegrationServiceId}/settings`
        const connectType = _STATELESS_INTEGRATION_CONNECT_TYPES[integrationType as IntegrationServiceId]
        createNewUserIntegration(accountId, integrationType as IntegrationServiceId, {
          code,
          redirectUri: statelessRedirectUri,
          connectType,
        })
      } else {
        handleError()
      }
      return
    }

    const state = searchParams.get('state')
    if (!state) {
      handleError()
      return
    }
    const stateData = readState(state, integrationType as IntegrationServiceId)
    if (!stateData) {
      handleError()
      return
    }

    const { step, customDomain, environment, redirectUri } = stateData

    if (code) {
      if (usesCustomDomainSSO(integrationType as IntegrationServiceId) && step === `${integrationType}_discovery_step`) {
        const state = createState(redirectUri, `${integrationType}_oauth_step`, customDomain)
        window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${integrationType}/enable?redirect_uri=${payloadRedirectUri}&code=${code}&state=${state}`
        return
      }

      const payload = {
        redirectUri: payloadRedirectUri,
        code,
        ...rest,
        ...(customDomain && { customDomain }),
        ...(usesCustomDomainSSO(integrationType as IntegrationServiceId) && { connectType: 'oauth_custom_domain' }),
        ...((integrationType === IntegrationServiceId.EXCHANGE_CALENDAR || integrationType === IntegrationServiceId.EXCHANGE_MAIL) && {
          connectType: 'oauth_custom_domain',
        }),
        ...(integrationType === IntegrationServiceId.WEALTH && state && { state, connectType: 'state_oauth' }),
        ...(integrationType === IntegrationServiceId.CONQUEST_PLANNING && {
          environment,
          connectType: 'oauth_custom_domain',
        }),
      }

      createNewUserIntegration(accountId, integrationType as IntegrationServiceId, payload)
    } else {
      handleError()
    }
  }, [navigate, searchParams, accountId, integrationType, createNewUserIntegration, createPayloadFromSearchParams, handleError])

  return <RedirectProgress />
}

export default IntegrationCallbackView
